import React, { useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { Box, Grid, Paper, Theme } from '@mui/material'
import { makeStyles } from '@mui/styles'

import KeyboardArrowLeftRounded from '@mui/icons-material/KeyboardArrowLeftRounded'
import VisibilityRounded from '@mui/icons-material/VisibilityRounded'

import Cropper from 'react-cropper'

import ButtonComponent from '@professocial/professocialFormComponents/FormComponents/ButtonComponent'
import LightBox from '@professocial/professocialFormComponents/FormComponents/LightBox'
import MediaGalleryComponent from '@professocial/professocialFormComponents/FormComponents/MediaGallery/MediaGalleryComponent'
import MediaCropperComponent from '@professocial/professocialFormComponents/FormComponents/MediaCropperComponent'

import {
  NetworkRatioSelection,
  PostingError,
  PostingErrorCode,
  PostingScreen,
  Restrictions,
} from '../../../../types/types'
import { GeneralPostingOptions, PostingTemplate, PostTypeEnum, PreviewFormat } from '@professocial/professocialTypes/types/commonTypes'
import { MediaFileMetadata, MediaFileSorting } from '@3m5/sm_publish-frontend-api'
import { useDispatch, useSelector } from 'react-redux'
import {
  setUploadTemporaryMediaFile,
  uploadTemporaryMediaFileInitiated,
  listMediaFilesInitiated,
} from '../../../../../store/posting/posting.actions'
import { UploadTemporaryMediaFileRequest } from '@3m5/sm_publish-frontend-api'
import { getMediaData, getTemporaryMediaFile, isLoadingTMFile } from '../../../../../store/posting/posting.selector'
import DialogComponent from '@professocial/professocialFormComponents/FormComponents/DialogComponent'
import validateUpload from '../../../../../util/validateUpload'
import { getGeneralPostingOptions, getUser } from '../../../../../store/common/common.selectors'

const useStyles = makeStyles((theme: Theme) => ({
  paper: {
    marginTop: '10px',
    padding: '25px 30px',
  },
  button: {
    fontWeight: theme.typography.fontWeightBold,
    fontSize: theme.typography.body1.fontSize,
    textTransform: 'initial',
    marginRight: '20px',
  },
}))

interface IProps {
  posting: PostingTemplate
  previewFormat?: PreviewFormat
  restrictions?: Restrictions
  useMediaGallery?: boolean
  changePosting?: (post: PostingTemplate) => void
  changePostingScreen?: (value: PostingScreen) => void
}

const ImageVideo: React.FC<IProps> = (props) => {
  const classes = useStyles()
  const { t } = useTranslation()
  const dispatch = useDispatch()

  const useMediaGallery = !!props.useMediaGallery
  // todo Ratio für Instagram > später noch per EP vom Server anfragen
  const ratioSelection: NetworkRatioSelection = {
    facebook: [
      { label: 'Original', ratio: undefined },
      { label: '1:1', ratio: 1 },
      { label: '4:5', ratio: 4 / 5 },
      { label: '5:4', ratio: 5 / 4 },
      { label: '9:16', ratio: 9 / 16 },
      { label: '16:9', ratio: 16 / 9 },
    ],
    instagram: [
      { label: 'Original', ratio: undefined },
      { label: '1:1', ratio: 1 },
      { label: '4:5', ratio: 4 / 5 },
      { label: '16:9', ratio: 16 / 9 },
    ],
  }

  const [posting, setPosting] = useState<PostingTemplate>(props.posting)
  const [allowedExtensions, setAllowedExtensions] = useState<string[] | undefined>(undefined)
  const [selectedMedia, setSelectedMedia] = useState<MediaFileMetadata>()
  const [videoThumbnail, setVideoThumbnail] = useState<string>()

  const [tempImage, setTempImage] = useState<string | undefined>(undefined)

  const [image, setImage] = useState<string | undefined>()
  const [mediaFileType, setMediaFileType] = useState<'image' | 'video' | undefined>()
  const [preview, setPreview] = useState<MediaFileMetadata>()
  const [cropper, setCropper] = useState<any>()
  const [showPostingError, setShowPostingError] = useState<boolean>(false)
  const [errorMessages, setErrorMessages] = useState<PostingError[]>([])
  const [openRationSelection, setOpenRationSelection] = useState<boolean>(false)
  const [startUpload, setStartUpload] = useState<boolean>(false)
  const [socialMediaCounter, setSocialMediaCounter] = useState<number>(0)

  const ITEMS_PER_PAGE = 40

  const handleServerPageChange = useCallback((page: number) => {
    const limit = ITEMS_PER_PAGE
    const offset = (page - 1) * limit
    dispatch(listMediaFilesInitiated({ limit, offset, sort: MediaFileSorting.UploadedAt, sortDesc: true, onlyEditorFiles: user?.role === 'EDITOR' }))
  }, [dispatch])

  const dispatchUploadTemporaryMediaFile = useCallback((params: UploadTemporaryMediaFileRequest) => dispatch(uploadTemporaryMediaFileInitiated(params)), [dispatch])
  const dispatchSetTemporaryMediaFile = useCallback((params: MediaFileMetadata | undefined) => dispatch(setUploadTemporaryMediaFile(params)), [dispatch])

  const temporaryMediaData: MediaFileMetadata | undefined = useSelector(getTemporaryMediaFile)
  const mediaData = useSelector(getMediaData)
  const loadingTempImage: boolean | undefined = useSelector(isLoadingTMFile)
  const generalPostingOptions: GeneralPostingOptions | undefined = useSelector(getGeneralPostingOptions)
  const user = useSelector(getUser)

  const isVideo = selectedMedia && selectedMedia.mimeType && selectedMedia.mimeType.includes('video')

  useEffect(() => {
    if (loadingTempImage) {
      setStartUpload(true)
    }
    if (!loadingTempImage && startUpload && !temporaryMediaData) {
      onErrorNewUpload()
      setStartUpload(false)
    }
    if (!loadingTempImage && startUpload && temporaryMediaData) {
      setStartUpload(false)
    }
  }, [loadingTempImage, temporaryMediaData],
  )

  useEffect(() => {
    let extensions: string[] = []
    if (props.restrictions) {
      const socialMediaNetwork = posting.socialMediaNetwork
      Object.keys(socialMediaNetwork).map((network:string) => {
        if (socialMediaNetwork[network] && props.restrictions && props.restrictions[network]) {
          const allowedImageExtensions: string[] = props.restrictions[network].image?.allowedFileExtensions || []
          const allowedVideoExtensions: string[] = props.restrictions[network].video?.allowedFileExtensions || []
          extensions = extensions.concat(allowedImageExtensions)
          extensions = extensions.concat(allowedVideoExtensions)
        }
      })
    }
    setAllowedExtensions(extensions)
  }, [props.restrictions],
  )

  useEffect(() => {
    props.posting && setPosting(props.posting)
    let count = 0
    Object.keys(props.posting.socialMediaNetwork).map(elem => {
      if (props.posting.socialMediaNetwork[elem]) {
        count += 1
      }
    })
    setSocialMediaCounter(count)
  }, [props.posting],
  )

  useEffect(() => {
    temporaryMediaData && setPreview(temporaryMediaData)
    temporaryMediaData && temporaryMediaData.mimeType.includes('image') && setMediaFileType('image')
    temporaryMediaData && temporaryMediaData.mimeType.includes('video') && setMediaFileType('video')
  }, [temporaryMediaData])


  useEffect(() => {
    setPreview(undefined)
  }, [])

  const getCropData = () => {
    if (typeof cropper !== 'undefined') {
      const img = cropper.getCroppedCanvas().toDataURL('image/jpeg')

      const width = cropper.getCroppedCanvas({ rounded: true }).width
      const height = cropper.getCroppedCanvas({ rounded: true }).height

      const error: PostingError[] = props.restrictions ? validateUpload(img, width, height, props.posting.socialMediaNetwork, props.restrictions) : []

      setTempImage(img)
      if (error.length === 0) {
        uploadImage(img)
      } else {
        setErrorMessages(error)
        setShowPostingError(true)
      }
    }
  }

  const uploadImage = (img: string) => {
    fetch(img)
      .then(res => res.blob())
      .then(blob => dispatchUploadTemporaryMediaFile({ file: blob }))
  }

  const onChangeVideo = (video: string, file: File | undefined) => {
    fetch(video)
      .then(res => res.blob())
      .then(blob => dispatchUploadTemporaryMediaFile({ file: blob }))
    setImage(undefined)
  }

  const handleCropped = (instance: Cropper) => {
    setCropper(instance)
  }

  const onChangeImage = (img: string | undefined, file: File | undefined) => {
    setImage(img)
    setSelectedMedia(undefined)
  }

  const handleSelectedMedia = (item: MediaFileMetadata, thumbnail: string | undefined) => {
    !image && setSelectedMedia(item)
    thumbnail && setVideoThumbnail(thumbnail)
  }

  const selectMediaFile = () => {
    const newPostTemplate = { ...posting.content }
    if (image && preview) {
      newPostTemplate.image = { url: preview.directLink, mediaId: preview.mediaFileId }
      delete newPostTemplate.link
      delete newPostTemplate.video
    }
    if (!selectedMedia && preview && mediaFileType === 'video') {
      newPostTemplate.video = {
        previewImageUrl: '/',
        url: preview.directLink,
        mediaId: preview.mediaFileId,
      }
      delete newPostTemplate.link
      delete newPostTemplate.image
    }
    if (selectedMedia && selectedMedia.directLink && !isVideo) {
      newPostTemplate.image = { url: selectedMedia.directLink, mediaId: selectedMedia.mediaFileId }
    }
    if (selectedMedia && selectedMedia.directLink && isVideo) {
      newPostTemplate.video = {
        previewImageUrl: '/',
        url: selectedMedia.directLink,
        mediaId: selectedMedia.mediaFileId,
      }
    }

    const newPost = {
      ...posting,
      content: newPostTemplate,
      postType: isVideo ? PostTypeEnum.VIDEO : PostTypeEnum.PHOTO,
    }

    props.changePosting && props.changePosting(newPost)
    props.changePostingScreen && props.changePostingScreen(PostingScreen.MAIN)
  }

  const deleteImage = () => {
    setImage(undefined)
    setPreview(undefined)

    const newPostTemplate = posting.content
    delete newPostTemplate.image

    const newPost = {
      ...posting,
      postTemplate: newPostTemplate,
      postType: PostTypeEnum.MESSAGE,
    }
    dispatchSetTemporaryMediaFile(undefined)
    props.changePosting && props.changePosting(newPost)
  }

  const onErrorNewUpload = () => {
    setShowPostingError(false)
    setImage(undefined)
    setPreview(undefined)
    setErrorMessages([])
  }

  const onCancel = () => {
    setShowPostingError(false)
    setImage(undefined)
    setPreview(undefined)
    setErrorMessages([])
  }

  const onErrorUnselectNetwork = () => {
    setShowPostingError(false)
    const network = errorMessages[0].network
    let newPosting = posting

    if (network) {
      newPosting = {
        ...newPosting,
        socialMediaNetwork: {
          ...newPosting.socialMediaNetwork,
          [network.toLowerCase()]: false,
        },
      }
      const newErrorMessages: PostingError[] = []
      errorMessages.find((error: PostingError) => {
        if (error.network !== network) {
          newErrorMessages.push(error)
        }
      })
      setErrorMessages(newErrorMessages)
      props.changePosting && props.changePosting(newPosting)
      if (newErrorMessages.length === 0) {
        tempImage && uploadImage(tempImage)
      } else {
        setShowPostingError(true)
      }
    }
  }

  const onErrorNewRatio = () => {
    setShowPostingError(false)
    if (errorMessages[0].network) {
      cropper.setAspectRatio(ratioSelection[errorMessages[0].network][1].ratio)
    }
    setOpenRationSelection(true)
  }

  return (
    <>
      <ButtonComponent
        className={classes.button}
        startIcon={<KeyboardArrowLeftRounded />}
        variant='text'
        color='info'
        text={t('postingCommon:disable')}
        onClick={() => props.changePostingScreen && props.changePostingScreen(PostingScreen.MAIN)}
      />
      <Grid container>
        <Grid xs={12} item>
          <Paper className={classes.paper}>
            <MediaCropperComponent
              previewFormat={props.previewFormat || PreviewFormat.square}
              mediaFile={image}
              mediaFileType={mediaFileType}
              loading={loadingTempImage}
              initialAspectRatio={undefined}
              getCropData={getCropData}
              allowedExtensions={allowedExtensions}
              ratioSelection={posting.socialMediaNetwork.instagram ? ratioSelection.instagram : ratioSelection.facebook}
              openRationSelection={openRationSelection}
              handleCropped={handleCropped}
              deleteImage={deleteImage}
              onChangeImage={onChangeImage}
              allowVideoUpload={generalPostingOptions.allowVideoUpload}
              allowImageUpload={generalPostingOptions.allowImageUpload}
              onChangeVideo={onChangeVideo}
              preview={preview?.directLink}
              cropBoxResizable
            />
          </Paper>
        </Grid>
        <Grid xs={12} item>
          {useMediaGallery && mediaData &&
            <Paper className={classes.paper}>
              <MediaGalleryComponent
                mediaData={mediaData}
                setSelected={handleSelectedMedia}
                selected={selectedMedia}
                itemsProPage={ITEMS_PER_PAGE}
                rows={4}
                onPageChange={handleServerPageChange}
              />
            </Paper>}
        </Grid>

        <Box component='div' style={{ display: 'flex', width: '100%', justifyContent: 'flex-end', marginTop: '10px' }}>
          {useMediaGallery &&
            <LightBox
              src={isVideo && videoThumbnail ? videoThumbnail : selectedMedia?.directLink}
              videoSrc={isVideo ? selectedMedia?.directLink : undefined}
              disabled={!selectedMedia || Object.keys(selectedMedia).length === 0}
            >
              <ButtonComponent
                disabled={!selectedMedia || Object.keys(selectedMedia).length === 0}
                className={classes.button}
                startIcon={<VisibilityRounded />}
                color='info'
                variant='text'
                text={t('postingCommon:preview')}
              />
            </LightBox>}
          <ButtonComponent
            onClick={selectMediaFile}
            disabled={loadingTempImage || (!selectedMedia && !preview) || (selectedMedia && !preview && Object.keys(selectedMedia).length === 0)}
            text={t('postingCommon:useFile')}
          />
        </Box>
      </Grid>
      {errorMessages[0] && showPostingError &&
        <DialogComponent
          type='warning'
          message={
            errorMessages[0].errorCode === PostingErrorCode.errorImageRatioWrong
              ? socialMediaCounter > 1 ? t('postingCommon:wrongRatioNetwork') : t('postingCommon:wrongRatio')
              : (errorMessages[0].errorCode === PostingErrorCode.errorImageTooLarge
                ? socialMediaCounter > 1 ? t('postingCommon:fileTooLargeNetwork') : t('postingCommon:fileTooLarge')
                : '')
          }
          open={showPostingError}
          autoClose={false}
          cancelLabel={socialMediaCounter > 1 ? t('postingCommon:unselectNetworkButton') : t('postingCommon:cancel')}
          confirmLabel={
            errorMessages[0].errorCode === PostingErrorCode.errorImageRatioWrong
              ? t('postingCommon:changeRatioButton')
              : (errorMessages[0].errorCode === PostingErrorCode.errorImageTooLarge
                ? t('postingCommon:changeFileButton')
                : '')
          }
          requestActions
          onClose={() => setShowPostingError(false)}
          onCancel={() => socialMediaCounter > 1 ? onErrorUnselectNetwork() : onCancel()}
          onConfirm={() => {
            setShowPostingError(false)
            errorMessages[0].errorCode === PostingErrorCode.errorImageRatioWrong
              ? onErrorNewRatio()
              : (errorMessages[0].errorCode === PostingErrorCode.errorImageTooLarge && onErrorNewUpload())
          }}
        />}
    </>
  )
}

export default ImageVideo
